import type { Incident } from '../services/incidentsApi';
import {
  incidentCanonicalFollowUpStateLabel,
  incidentCanonicalFollowupOwnerLabel,
  incidentCanonicalRuleOwnerLabel,
} from '../components/incidents/reviewCanonical';
import { displaySafetyManagerLabel } from '../domain/safetyManagerUxLabels';

type CustomerStatusFact = {
  label: string;
  value: string;
};

type BuildCustomerFollowUpFactInput = {
  reportScopeIncidents: Array<Incident | Record<string, any>>;
  actionRecorded: boolean;
  outcomeRecorded: boolean;
};

type BuildCustomerStatusFactsInput = {
  customerStatusLabel: string;
  customerEvidenceFact: string;
  latestEventTimestampLabel: string;
  customerFollowUpFact: string;
};

type BuildCustomerWhatHappenedInput = {
  pedestrianZoneHero: unknown;
  customerSafetyRuleLabel: string;
  customerAreaLabel: string;
  customerIncidentLabel: string;
};

type BuildCustomerFollowupFactsInput = {
  ruleOwnerLabel: string;
  followupOwnerLabel: string;
  currentActionLabel: string;
  workflowDateLabel: string;
};

type BuildSafetyReportFollowupFactsInput = {
  patternOwnerLabel: string;
  followupOwnerLabel: string;
  currentActionLabel: string;
  workflowDateLabel: string;
  ruleOwnerLabel?: string | null;
};

type BuildCustomerUnprovenSummaryInput = {
  isLoopedDemoProofCase: boolean;
  demoOutcomeNote?: string | null;
  outcomeRecorded: boolean;
  /** When set (e.g. projection evidence_breakdown.readiness_label), tightens wording for limited readiness. */
  evidenceReadinessLabel?: string | null;
};

function customerEvidenceCapturedPhrase(evidenceReadinessLabel?: string | null): string {
  return String(evidenceReadinessLabel || '').trim() === 'Ready with limitations'
    ? 'review context was captured. Media evidence is not included in this export'
    : 'review context was captured';
}

export const CUSTOMER_REVIEW_CHECKLIST = [
  'Confirm whether this recurring risk pattern requires corrective action',
  `Assign ${displaySafetyManagerLabel('pattern_owner').toLowerCase()}`,
  'Assign action owner',
  'Record corrective action',
  'Complete impact measurement',
  'Mark report ready only after review is complete',
];

export function deriveCustomerOwnerLabel(
  reportScopeIncidents: Array<Incident | Record<string, any>>,
): string {
  for (const incident of reportScopeIncidents) {
    const owner = incidentCanonicalFollowupOwnerLabel(incident as Incident);
    if (owner) return owner;
  }
  return 'Not assigned in Review yet';
}

export function deriveCustomerRuleOwnerLabel(
  reportScopeIncidents: Array<Incident | Record<string, any>>,
): string {
  for (const incident of reportScopeIncidents) {
    const owner = incidentCanonicalRuleOwnerLabel(incident as Incident);
    if (owner) return owner;
  }
  return 'Not assigned';
}

export function deriveCustomerFollowUpFact({
  reportScopeIncidents,
  actionRecorded,
  outcomeRecorded,
}: BuildCustomerFollowUpFactInput): string {
  if (outcomeRecorded) return 'Follow-up recorded';
  for (const incident of reportScopeIncidents) {
    const canonicalState = incidentCanonicalFollowUpStateLabel(incident as Incident);
    if (canonicalState) return canonicalState;
  }
  if (actionRecorded) return 'Action logged, follow-up pending';
  return 'Action not yet confirmed';
}

export function buildCustomerStatusFacts({
  customerStatusLabel,
  customerEvidenceFact,
  latestEventTimestampLabel,
  customerFollowUpFact,
}: BuildCustomerStatusFactsInput): CustomerStatusFact[] {
  return [
    { label: 'Status', value: customerStatusLabel },
    { label: 'Evidence', value: customerEvidenceFact },
    { label: 'Latest event', value: latestEventTimestampLabel === 'Not captured yet' ? 'Not available yet' : latestEventTimestampLabel },
    { label: 'Follow-up', value: customerFollowUpFact },
  ];
}

export function buildCustomerWhatHappened({
  pedestrianZoneHero,
  customerSafetyRuleLabel,
}: BuildCustomerWhatHappenedInput): string {
  if (pedestrianZoneHero || customerSafetyRuleLabel === 'Pedestrian Activity in Forklift Zone') {
    return 'Camera observations were grouped into a recurring risk pattern during the selected time window, including pedestrian activity in the restricted forklift operating area.';
  }
  return 'Camera observations were grouped into a recurring risk pattern during the selected time window.';
}

export function buildCustomerReviewStatusSummary(
  proofPageState: string,
  outcomeRecorded: boolean,
): string {
  if (proofPageState === 'GENERATING') {
    return 'Evidence is still being prepared, so final operational review is not complete yet.';
  }
  if (outcomeRecorded) {
    return 'Evidence and impact measurement have been recorded, but longer-term confirmation should still be reviewed.';
  }
  return 'Representative evidence is available, but human review is not complete.';
}

export function buildCustomerFollowupFacts({
  ruleOwnerLabel,
  followupOwnerLabel,
  currentActionLabel,
  workflowDateLabel,
}: BuildCustomerFollowupFactsInput): CustomerStatusFact[] {
  return buildSafetyReportFollowupFacts({
    patternOwnerLabel: ruleOwnerLabel,
    followupOwnerLabel,
    currentActionLabel,
    workflowDateLabel,
    ruleOwnerLabel,
  });
}

export function buildSafetyReportFollowupFacts({
  patternOwnerLabel,
  followupOwnerLabel,
  currentActionLabel,
  workflowDateLabel,
  ruleOwnerLabel,
}: BuildSafetyReportFollowupFactsInput): CustomerStatusFact[] {
  const facts: CustomerStatusFact[] = [
    { label: displaySafetyManagerLabel('pattern_owner'), value: patternOwnerLabel || 'Not assigned' },
    { label: displaySafetyManagerLabel('followup_owner'), value: followupOwnerLabel || 'Not assigned' },
    { label: 'Current action', value: currentActionLabel },
    { label: 'Workflow date', value: workflowDateLabel },
  ];
  const ruleOwner = String(ruleOwnerLabel || '').trim();
  const patternOwner = String(patternOwnerLabel || '').trim();
  if (ruleOwner && ruleOwner !== patternOwner && ruleOwner !== 'Not assigned') {
    facts.push({ label: `${displaySafetyManagerLabel('pattern_owner')} (rule config)`, value: ruleOwner });
  }
  return facts;
}

export function buildCustomerUnprovenSummary({
  isLoopedDemoProofCase,
  demoOutcomeNote,
  outcomeRecorded,
  evidenceReadinessLabel,
}: BuildCustomerUnprovenSummaryInput): string {
  const captured = customerEvidenceCapturedPhrase(evidenceReadinessLabel);
  if (isLoopedDemoProofCase) {
    return demoOutcomeNote
      || `This report shows that a recurring risk pattern was observed and ${captured}. It does not yet prove that risk was reduced over time because the measured period does not reflect stable operating conditions.`;
  }
  if (outcomeRecorded) {
    return `This report shows that a recurring risk pattern was observed, ${captured}, and impact measurement was recorded. Longer-term risk reduction should still be confirmed with continued measurement.`;
  }
  return 'This report shows that a recurring risk pattern was observed and that review context was captured. It does not yet prove that risk was reduced, because impact measurement has not been recorded. Media evidence is not included in this export.';
}
